import React, { useState, useEffect, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Chip,
  Typography,
} from "@material-ui/core";
import { makeStyles, alpha, lighten, useTheme } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Alert from "@material-ui/lab/Alert";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import ContactAvatar from "../ContactAvatar";
import formatPhone from "../../helpers/formatPhone";
import { AuthContext } from "../../context/Auth/AuthContext";
import { FormDialogHeader, FormSection, useFormDialogStyles } from "../FormDialog";
import TaskEditor from "../TaskEditor";

const emptyForm = {
  title: "",
  value: "",
  valueDisplay: "",
  userId: "",
  expectedCloseDate: "",
  notes: "",
  stageId: "",
};

const QUICK_LOST_REASONS = ["Preço", "Escolheu concorrente", "Sem resposta", "Sem orçamento"];

// converte "1.234,56" → 1234.56 (mesmo padrão do TransactionModal)
const parseBRL = (str) => {
  const clean = str.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(clean);
  return isNaN(n) ? "" : n;
};

const formatBRL = (digits) => {
  const n = parseInt(digits.replace(/\D/g, ""), 10) || 0;
  return (n / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const STATUS_LABEL = { won: "Ganho", lost: "Perdido" };

// "hoje", "ontem", "há 4 dias" (o moment do app não está em pt-BR)
const sinceLabel = (date) => {
  const days = moment().startOf("day").diff(moment(date).startOf("day"), "days");
  if (days <= 0) return "hoje";
  if (days === 1) return "ontem";
  return `há ${days} dias`;
};

const useStyles = makeStyles((theme) => ({
  contactCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  contactText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  contactName: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  twoCols: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: 0,
    },
  },
  steps: {
    display: "flex",
    gap: 2,
  },
  step: {
    flex: 1,
    minWidth: 0,
    height: 34,
    border: 0,
    cursor: "pointer",
    padding: theme.spacing(0, 1.5),
    font: "600 12px Inter, Roboto, sans-serif",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    clipPath: "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)",
    "&:first-child": {
      clipPath: "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)",
      borderRadius: "8px 0 0 8px",
    },
    "&:disabled": { cursor: "default" },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  closeButtons: {
    display: "flex",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { flexDirection: "column" },
  },
  closeButton: {
    flex: 1,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
  },
  lostBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
    backgroundColor: alpha(theme.palette.error.main, 0.06),
  },
  reasonChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  history: {
    display: "flex",
    flexDirection: "column",
  },
  historyItem: {
    display: "flex",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 0),
  },
  historyDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginTop: 5,
    flexShrink: 0,
  },
  statusChip: {
    height: 24,
    fontWeight: 600,
    fontSize: 12,
  },
  deleteButton: {
    marginRight: "auto",
    textTransform: "none",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    "&:hover": { color: theme.palette.error.main },
  },
}));

const OpportunityModal = ({
  open,
  onClose,
  opportunity,
  initialContact,
  pipelineId: pipelineIdProp,
  initialStageId,
}) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";
  const tone = (c) => (theme.palette.type === "dark" ? lighten(c, 0.25) : c);

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  // detalhe do negócio (com histórico de etapas) — só na edição
  const [detail, setDetail] = useState(null);
  const [moving, setMoving] = useState(false);
  const [changed, setChanged] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [taskOpen, setTaskOpen] = useState(false);

  // negócios já abertos do contato escolhido — evita duplicata acidental
  // quando a automação de entrada já criou um card para ele
  const [openOpportunities, setOpenOpportunities] = useState([]);

  // seleção de contato (só quando não há contato fixo nem edição)
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [searching, setSearching] = useState(false);

  const fixedContact = opportunity?.contact || initialContact || null;
  const contactId = fixedContact?.id || selectedContact?.id || null;
  const current = detail || opportunity;

  useEffect(() => {
    if (!open) return;

    const loadUsers = async () => {
      try {
        const { data } = await api.get("/users/");
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    const loadPipelines = async () => {
      try {
        const { data } = await api.get("/pipelines");
        setPipelines(data || []);
      } catch (err) {
        // sem funis o modal só não mostra as etapas
      }
    };
    loadUsers();
    loadPipelines();

    if (opportunity) {
      const rawValue = opportunity.value || 0;
      const digits = Math.round(Number(rawValue) * 100).toString();
      setForm({
        ...emptyForm,
        title: opportunity.title || "",
        value: rawValue || "",
        valueDisplay: rawValue ? formatBRL(digits) : "",
        userId: opportunity.userId || "",
        expectedCloseDate: opportunity.expectedCloseDate || "",
        notes: opportunity.notes || "",
      });
    } else {
      setForm({ ...emptyForm, stageId: initialStageId || "" });
    }
    setDetail(null);
    setChanged(false);
    setLostOpen(false);
    setLostReason("");
    setSelectedContact(null);
    setContactSearch("");
    setConfirmDelete(false);
  }, [open, opportunity, initialStageId]);

  // detalhe com histórico de etapas
  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/opportunities/${id}`);
      setDetail(data);
    } catch (err) {
      // o modal funciona sem o histórico
    }
  };

  useEffect(() => {
    if (open && opportunity?.id) loadDetail(opportunity.id);
  }, [open, opportunity]);

  // aviso de duplicata: só na criação, quando já existe negócio aberto
  useEffect(() => {
    if (!open || opportunity || !contactId) {
      setOpenOpportunities([]);
      return;
    }
    const loadOpen = async () => {
      try {
        const { data } = await api.get("/opportunities", {
          params: { contactId, status: "open" },
        });
        setOpenOpportunities(data.opportunities || []);
      } catch (err) {
        // o aviso é conveniência — falha aqui não pode bloquear a criação
      }
    };
    loadOpen();
  }, [open, opportunity, contactId]);

  useEffect(() => {
    if (!open || fixedContact || contactSearch.length < 2) return;
    setSearching(true);
    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", {
          params: { searchParam: contactSearch },
        });
        setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      } finally {
        setSearching(false);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [contactSearch, open, fixedContact]);

  // funil do negócio (edição) ou o escolhido/padrão (criação)
  const pipeline = useMemo(() => {
    const wanted = current?.pipelineId || pipelineIdProp;
    return (
      pipelines.find((p) => p.id === wanted) ||
      pipelines.find((p) => p.isDefault) ||
      pipelines[0] ||
      null
    );
  }, [pipelines, current, pipelineIdProp]);

  const stages = useMemo(
    () => (pipeline?.stages || []).slice().sort((a, b) => a.order - b.order),
    [pipeline]
  );
  const openStages = stages.filter((s) => s.type === "open");
  const wonStage = stages.find((s) => s.type === "won");
  const lostStage = stages.find((s) => s.type === "lost");

  const handleClose = () => onClose(changed || undefined);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleValueChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (!digits) {
      setForm((prev) => ({ ...prev, value: "", valueDisplay: "" }));
      return;
    }
    const display = formatBRL(digits);
    setForm((prev) => ({ ...prev, value: parseBRL(display), valueDisplay: display }));
  };

  // troca de etapa é imediata (mesma regra do arrastar no quadro)
  const moveTo = async (stageId, reason) => {
    if (!current?.id || stageId === current.stageId) return;
    setMoving(true);
    try {
      await api.put(`/opportunities/${current.id}/stage`, {
        stageId,
        lostReason: reason,
      });
      setChanged(true);
      setLostOpen(false);
      setLostReason("");
      await loadDetail(current.id);
    } catch (err) {
      toastError(err);
    } finally {
      setMoving(false);
    }
  };

  const confirmLost = () => {
    if (!lostReason.trim()) {
      toast.error("Informe o motivo da perda.");
      return;
    }
    moveTo(lostStage.id, lostReason.trim());
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/opportunities/${current.id}`);
      toast.success("Negócio excluído.");
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Informe o título do negócio.");
      return;
    }
    if (!opportunity && !contactId) {
      toast.error("Selecione o contato do negócio.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: form.title,
        value: form.value === "" ? null : form.value,
        userId: form.userId === "" ? null : form.userId,
        expectedCloseDate: form.expectedCloseDate || null,
        notes: form.notes || null,
      };

      let saved;
      if (opportunity) {
        const { data } = await api.put(`/opportunities/${opportunity.id}`, payload);
        saved = data;
        toast.success("Negócio atualizado.");
      } else {
        const { data } = await api.post("/opportunities", {
          ...payload,
          contactId,
          pipelineId: pipeline?.id,
          stageId: form.stageId || undefined,
        });
        saved = data;
        toast.success("Negócio criado.");
      }
      onClose(saved);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const status = current?.status || "open";
  const currentStage = current && stages.find((s) => s.id === current.stageId);
  const statusChip = current
    ? status === "open"
      ? { label: currentStage?.name || current.stage?.name || "Em andamento", color: currentStage?.color || "#1E88E5" }
      : { label: STATUS_LABEL[status], color: status === "won" ? "#43A047" : "#E53935" }
    : null;
  const currentOpenIndex = openStages.findIndex((s) => s.id === current?.stageId);

  const logs = detail?.stageLogs || [];

  const renderContact = (contact) => (
    <div className={classes.contactCard}>
      <ContactAvatar src={contact.profilePicUrl} name={contact.name} style={{ width: 40, height: 40 }} />
      <div className={classes.contactText}>
        <span className={classes.contactName}>{contact.name}</span>
        <span className={classes.muted}>
          {contact.number ? formatPhone(contact.number) : "Sem WhatsApp"}
        </span>
      </div>
      <Button
        color="primary"
        size="small"
        className={dialogClasses.textButton}
        onClick={() => {
          onClose(changed || undefined);
          history.push(`/contacts/${contact.id}/profile`);
        }}
      >
        Abrir ficha
      </Button>
      {!fixedContact && !opportunity && (
        <Button size="small" className={dialogClasses.textButton} onClick={() => setSelectedContact(null)}>
          Trocar
        </Button>
      )}
    </div>
  );

  return (
    <>
      <ConfirmationModal
        title="Excluir negócio"
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      >
        O negócio "{current?.title}" e todo o histórico de movimentação dele
        serão apagados, inclusive dos relatórios do funil. As conversas
        vinculadas não são afetadas.
      </ConfirmationModal>

      {opportunity && (
        <TaskEditor
          open={taskOpen}
          onClose={() => setTaskOpen(false)}
          initialOpportunity={{ ...current, contact: current?.contact || opportunity.contact }}
        />
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: dialogClasses.paper }}
        aria-labelledby="opportunity-title"
      >
        <FormDialogHeader
          id="opportunity-title"
          title={
            opportunity ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {current?.title || "Negócio"}
                {statusChip && (
                  <Chip
                    size="small"
                    label={statusChip.label}
                    className={classes.statusChip}
                    style={{ color: tone(statusChip.color), backgroundColor: alpha(statusChip.color, 0.16) }}
                  />
                )}
              </span>
            ) : (
              "Novo negócio"
            )
          }
          subtitle={
            opportunity
              ? [
                  current?.contact?.name,
                  current?.createdAt && `criado em ${moment(current.createdAt).format("DD/MM/YYYY")}`,
                  current?.updatedAt && `atualizado ${sinceLabel(current.updatedAt)}`,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : "Registre uma oportunidade de venda ligada a um contato."
          }
          onClose={handleClose}
          disabled={loading}
        />

        <DialogContent dividers className={dialogClasses.content}>
          {/* Etapa (edição) */}
          {opportunity && stages.length > 0 && (
            <FormSection title="Etapa no funil">
              {openStages.length > 0 && (
                <div className={classes.steps} role="group" aria-label="Etapa">
                  {openStages.map((stage, index) => {
                    const isCurrent = status === "open" && stage.id === current?.stageId;
                    const passed = status === "open" && index <= currentOpenIndex;
                    return (
                      <button
                        key={stage.id}
                        type="button"
                        className={classes.step}
                        aria-pressed={isCurrent}
                        title={`Mover para ${stage.name}`}
                        disabled={moving}
                        onClick={() => moveTo(stage.id)}
                        style={
                          passed
                            ? {
                                backgroundColor: alpha(stage.color || "#1E88E5", isCurrent ? 0.9 : 0.35),
                                color: isCurrent ? "#10151F" : theme.palette.text.primary,
                              }
                            : {
                                backgroundColor: theme.palette.action.selected,
                                color: theme.palette.text.secondary,
                              }
                        }
                      >
                        {stage.name}
                      </button>
                    );
                  })}
                </div>
              )}
              {(wonStage || lostStage) && (
                <div className={classes.closeButtons}>
                  {wonStage && (
                    <Button
                      variant="outlined"
                      className={classes.closeButton}
                      startIcon={<CheckIcon />}
                      disabled={moving || current?.stageId === wonStage.id}
                      onClick={() => moveTo(wonStage.id)}
                      style={{ color: tone("#43A047"), borderColor: alpha("#43A047", 0.5), backgroundColor: alpha("#43A047", 0.08) }}
                    >
                      {status === "won" ? "Ganho" : "Marcar como ganho"}
                    </Button>
                  )}
                  {lostStage && (
                    <Button
                      variant="outlined"
                      className={classes.closeButton}
                      startIcon={<CloseIcon />}
                      disabled={moving || current?.stageId === lostStage.id}
                      onClick={() => setLostOpen((v) => !v)}
                      style={{ color: tone("#E53935"), borderColor: alpha("#E53935", 0.5), backgroundColor: alpha("#E53935", 0.06) }}
                    >
                      {status === "lost" ? "Perdido" : "Marcar como perdido"}
                    </Button>
                  )}
                </div>
              )}
              {status === "lost" && current?.lostReason && (
                <Typography variant="body2" className={classes.muted}>
                  Motivo da perda: <strong>{current.lostReason}</strong>
                </Typography>
              )}
              {lostOpen && lostStage && (
                <div className={classes.lostBox}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    Por que este negócio foi perdido?
                  </Typography>
                  <div className={classes.reasonChips}>
                    {QUICK_LOST_REASONS.map((reason) => (
                      <Chip
                        key={reason}
                        size="small"
                        clickable
                        label={reason}
                        color={lostReason === reason ? "secondary" : "default"}
                        variant={lostReason === reason ? "default" : "outlined"}
                        onClick={() => setLostReason(reason)}
                      />
                    ))}
                  </div>
                  <TextField
                    value={lostReason}
                    onChange={(e) => setLostReason(e.target.value)}
                    placeholder="Ou descreva o motivo…"
                    variant="outlined"
                    size="small"
                    fullWidth
                    autoFocus
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button size="small" className={dialogClasses.textButton} onClick={() => setLostOpen(false)}>
                      Cancelar
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      className={dialogClasses.textButton}
                      onClick={confirmLost}
                      disabled={moving}
                      style={{ backgroundColor: theme.palette.error.main, color: "#fff" }}
                    >
                      Confirmar perda
                    </Button>
                  </div>
                </div>
              )}
            </FormSection>
          )}

          {/* Contato */}
          <FormSection title="Contato">
            {fixedContact ? (
              renderContact(fixedContact)
            ) : selectedContact ? (
              renderContact(selectedContact)
            ) : (
              <Autocomplete
                options={contactOptions}
                loading={searching}
                value={selectedContact}
                getOptionLabel={(option) => option.name || ""}
                onChange={(e, newValue) => setSelectedContact(newValue)}
                onInputChange={(e, newInputValue) => setContactSearch(newInputValue)}
                noOptionsText={contactSearch.length < 2 ? "Digite pelo menos 2 letras" : "Nenhum contato encontrado"}
                renderOption={(option) => (
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <ContactAvatar src={option.profilePicUrl} name={option.name} style={{ width: 32, height: 32, fontSize: 14 }} />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontWeight: 600 }}>{option.name}</span>
                      <span className={classes.muted}>
                        {option.number ? formatPhone(option.number) : "Sem WhatsApp"}
                      </span>
                    </div>
                  </div>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    margin="dense"
                    autoFocus
                    placeholder="Busque o contato pelo nome ou número"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {openOpportunities.length > 0 && (
              <Alert severity="warning" style={{ borderRadius: 10 }}>
                Este contato já tem{" "}
                {openOpportunities.length === 1
                  ? "um negócio aberto"
                  : `${openOpportunities.length} negócios abertos`}
                :{" "}
                <strong>
                  {openOpportunities
                    .map((o) => (o.stage?.name ? `${o.title} (${o.stage.name})` : o.title))
                    .join(", ")}
                </strong>
                . Criar outro gera um card separado no funil.
              </Alert>
            )}
          </FormSection>

          {/* Negócio */}
          <FormSection title="Negócio">
            <TextField
              label="Título"
              required
              value={form.title}
              onChange={handleChange("title")}
              fullWidth
              variant="outlined"
              margin="dense"
              placeholder="Ex.: Plano anual 5 usuários"
              InputLabelProps={{ shrink: true }}
            />
            <div className={classes.twoCols}>
              <TextField
                label="Valor"
                value={form.valueDisplay}
                onChange={handleValueChange}
                fullWidth
                variant="outlined"
                margin="dense"
                placeholder="0,00"
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
              <TextField
                label="Previsão de fechamento"
                type="date"
                value={form.expectedCloseDate}
                onChange={handleChange("expectedCloseDate")}
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </div>
            {!opportunity && openStages.length > 0 && (
              <TextField
                select
                label="Etapa inicial"
                value={form.stageId}
                onChange={handleChange("stageId")}
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <span className={classes.muted}>Primeira etapa ({openStages[0].name})</span>
                </MenuItem>
                {openStages.map((stage) => (
                  <MenuItem key={stage.id} value={stage.id}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        marginRight: 10,
                        display: "inline-block",
                        backgroundColor: stage.color || "#9E9E9E",
                      }}
                    />
                    {stage.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              label="Responsável"
              value={form.userId}
              onChange={handleChange("userId")}
              fullWidth
              variant="outlined"
              margin="dense"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <span className={classes.muted}>Sem responsável</span>
              </MenuItem>
              {users.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Observações"
              value={form.notes}
              onChange={handleChange("notes")}
              fullWidth
              variant="outlined"
              margin="dense"
              multiline
              minRows={3}
              placeholder="Contexto, próximos passos, objeções…"
              InputLabelProps={{ shrink: true }}
            />
          </FormSection>

          {/* Tarefa ligada ao negócio (edição) */}
          {opportunity && (
            <FormSection
              title="Próximo passo"
              action={
                <Button
                  size="small"
                  color="primary"
                  className={dialogClasses.textButton}
                  onClick={() => setTaskOpen(true)}
                >
                  + Nova tarefa
                </Button>
              }
            >
              <Typography variant="body2" className={classes.muted}>
                Crie uma tarefa já vinculada a este negócio e ao contato.
              </Typography>
            </FormSection>
          )}

          {/* Histórico de etapas (edição) */}
          {opportunity && logs.length > 0 && (
            <FormSection title="Histórico de etapas">
              <div className={classes.history}>
                {logs.map((log) => (
                  <div key={log.id} className={classes.historyItem}>
                    <span
                      className={classes.historyDot}
                      style={{ backgroundColor: log.toStage?.color || "#9E9E9E" }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 14 }}>
                        {log.fromStageId ? "Movido para" : "Criado em"}{" "}
                        <strong>{log.toStage?.name || "etapa removida"}</strong>
                        {log.fromStage?.name ? ` (de ${log.fromStage.name})` : ""}
                      </span>
                      <span className={classes.muted} style={{ fontSize: 12 }}>
                        {moment(log.createdAt).format("DD/MM/YYYY [às] HH:mm")}
                        {" · "}
                        {log.changedByUser?.name ? `por ${log.changedByUser.name}` : "automático"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </FormSection>
          )}
        </DialogContent>

        <DialogActions className={dialogClasses.actions}>
          {opportunity && isAdmin && (
            <Button
              onClick={() => setConfirmDelete(true)}
              disabled={loading}
              startIcon={<DeleteOutlineIcon />}
              className={classes.deleteButton}
            >
              Excluir
            </Button>
          )}
          <Button
            onClick={handleClose}
            disabled={loading}
            className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disableElevation
            disabled={loading}
            className={`${dialogClasses.actionButton} ${dialogClasses.progressWrapper}`}
          >
            {opportunity ? "Salvar alterações" : "Criar negócio"}
            {loading && <CircularProgress size={24} className={dialogClasses.buttonProgress} />}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OpportunityModal;
